import {Component, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {from} from "rxjs"
import {RouterOutlet} from "@angular/router"
import {CreateItemComponent} from "@common/components/base/create-item.component"
import {CheckboxesFilterComponent, PinnedFilterComponent, PinnedFilterOptionComponent, TagSearchFilterComponent} from "@common/components/filters"
import {ItemFiltersComponent} from "@common/components/item"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {Enums} from "@enums"
import {Labels, StateLabel} from "@labels"
import {ModelsPageVisibleUsersGQL} from "@pages/models/models-page.generated"
import {SidebarLayoutComponent} from "@platform/components/layouts/sidebar-layout/sidebar-layout.component"
import {ModelsGridComponent} from "@platform/components/models/models-grid/models-grid.component"
import {AddModelDialogComponent} from "@platform/components/models/add-model-dialog/add-model-dialog.component"
import {IsDefined} from "@cm/utils/filter"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-models-page",
    imports: [
        CheckboxesFilterComponent,
        ItemFiltersComponent,
        PinnedFilterComponent,
        PinnedFilterOptionComponent,
        RouterOutlet,
        SidebarLayoutComponent,
        TagSearchFilterComponent,
        ModelsGridComponent,
    ],
    templateUrl: "./models-page.component.html",
    styleUrl: "./models-page.component.scss",
})
export class ModelsPageComponent extends CreateItemComponent<AddModelDialogComponent> implements OnInit {
    readonly permission = inject(PermissionsService)
    $can = this.permission.$to

    readonly visibleUsersGql = inject(ModelsPageVisibleUsersGQL)

    readonly $userLabels = signal<StateLabel<string>[] | null>(null)

    ngOnInit() {
        from(fetchThrowingErrors(this.visibleUsersGql)({}))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({users}) => {
                this.$userLabels.set(
                    users.filter(IsDefined).map((user) => ({
                        label: user.name,
                        state: user.id,
                    })),
                )
            })
    }

    protected openCreateDialog() {
        const dialogRef = this.dialog.open(AddModelDialogComponent, {
            data: {},
        })
        this.setDialogRef(dialogRef)
    }

    protected override handleDialogResult(result: unknown): boolean | void {
        // Dialog returns the created model with {id, legacyId}
        const model = result as {id: string; legacyId: number} | undefined
        if (model?.id) {
            // Dialog created the model and returned it, navigate to it
            void this.router.navigate(["/models", model.id], {
                queryParamsHandling: "preserve",
            })
            return false // Prevent default navigation back
        }
        // Dialog was cancelled, allow default navigation back
    }

    protected readonly Enums = Enums
    protected readonly Labels = Labels
}
